import {useAppDataStore} from "@/mobx/appDataStore.jsx";
import {useState, useMemo, useCallback, useEffect} from "react";
import {observer} from "mobx-react";
import moment from "moment";
import formatters from "helpers/formatters.jsx";

const CHART_COLORS = ['#3b82f6', '#22c55e', '#a855f7', '#f97316', '#ef4444', '#eab308', '#6366f1', '#ec4899', '#14b8a6'];

export default observer(function SalesChart() {
  const appDataStore = useAppDataStore();
  const [chartStartDate, setChartStartDate] = useState(moment().startOf('day').format('YYYY-MM-DD'));
  const [chartEndDate, setChartEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [tooltip, setTooltip] = useState(null);

  const validityLabel = v => v ? `${v} ${parseInt(v) === 1 ? 'mes' : 'meses'}` : '';

  const loadChartData = useCallback(async (startDate, endDate) => {
    setChartLoading(true);
    try {
      const response = await appDataStore.post('/sales/chart-data', {
        startDate: startDate ?? chartStartDate,
        endDate: endDate ?? chartEndDate,
      });
      setChartData(response.data || []);
    } catch (err) {
      console.error('Error cargando datos del gráfico:', err);
    } finally {
      setChartLoading(false);
    }
  }, [appDataStore, chartStartDate, chartEndDate]);

  useEffect(() => { loadChartData(); }, []);

  const {byDistributor, categoryColorMap, maxCount, byProduct} = useMemo(() => {
    const categoryKey = d => `${d.productName}|${d.validity ?? ''}|${d.unitAmount}`;
    const distributors = [...new Set(chartData.map(d => d.distributorName))].sort();
    const allKeys = [...new Set(chartData.map(categoryKey))].sort();
    const categoryColorMap = Object.fromEntries(allKeys.map((c, i) => [c, CHART_COLORS[i % CHART_COLORS.length]]));
    const byDistributor = distributors.map(dist => ({
      name: dist,
      items: chartData.filter(d => d.distributorName === dist).map(d => ({...d, category: categoryKey(d)})),
      totalCount: chartData.filter(d => d.distributorName === dist).reduce((s, d) => s + d.count, 0),
    }));
    const maxCount = Math.max(...chartData.map(d => d.count), 1);

    const byProduct = Object.values(
      chartData.reduce((acc, d) => {
        const key = categoryKey(d);
        if (!acc[key]) acc[key] = {productName: d.productName, validity: d.validity, unitAmount: d.unitAmount, count: 0, totalAmount: 0};
        acc[key].count += d.count;
        acc[key].totalAmount += d.totalAmount;
        return acc;
      }, {})
    ).sort((a, b) => b.totalAmount - a.totalAmount);

    return {byDistributor, categoryColorMap, maxCount, byProduct};
  }, [chartData]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <h3 className="text-2xl font-bold text-gray-800 mb-4">Ventas por Distribuidor</h3>

      {/* Filtros de fecha */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-1">
          {[
            { label: 'Hoy', start: moment().startOf('day'), end: moment() },
            { label: 'Ayer', start: moment().subtract(1, 'day').startOf('day'), end: moment().subtract(1, 'day').endOf('day') },
            { label: 'Mes actual', start: moment().startOf('month'), end: moment() },
            { label: 'Mes anterior', start: moment().subtract(1, 'month').startOf('month'), end: moment().subtract(1, 'month').endOf('month') },
          ].map(({ label, start, end }) => (
            <button
              key={label}
              onClick={() => {
                const s = start.format('YYYY-MM-DD');
                const e = end.format('YYYY-MM-DD');
                setChartStartDate(s);
                setChartEndDate(e);
                loadChartData(s, e);
              }}
              disabled={chartLoading}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-100 text-gray-600 font-medium disabled:opacity-50"
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 font-medium">Desde:</label>
          <input
            type="date"
            value={chartStartDate}
            onChange={e => setChartStartDate(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 font-medium">Hasta:</label>
          <input
            type="date"
            value={chartEndDate}
            onChange={e => setChartEndDate(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={() => loadChartData()}
          disabled={chartLoading}
          className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 font-medium"
        >
          {chartLoading ? 'Cargando...' : 'Actualizar'}
        </button>
      </div>

      {chartLoading && (
        <div className="text-center py-10 text-gray-500 text-sm">Cargando datos...</div>
      )}

      {!chartLoading && chartData.length === 0 && (
        <div className="text-center py-10 text-gray-400 text-sm">No hay ventas exitosas en el período seleccionado.</div>
      )}

      {!chartLoading && chartData.length > 0 && (
        <>
          {/* Tooltip fixed */}
          {tooltip && (
            <div
              className="fixed bg-gray-900 text-white text-xs rounded-lg p-2.5 whitespace-nowrap z-50 shadow-xl pointer-events-none"
              style={{left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, -110%)'}}
            >
              <div className="font-semibold mb-1">{tooltip.item.productName}</div>
              {tooltip.item.validity && <div>Vigencia: {validityLabel(tooltip.item.validity)}</div>}
              <div>Monto unit.: {formatters.cash(tooltip.item.unitAmount)}</div>
              <div>Cantidad: <span className="font-bold">{tooltip.item.count}</span></div>
              <div>Total cobrado: {formatters.cash(tooltip.item.totalAmount)}</div>
            </div>
          )}

          {/* Gráfica de barras */}
          <div className="overflow-x-auto mb-6">
            <div className="flex items-end gap-8 min-w-max" style={{height: '280px', paddingBottom: '2px'}}>
              {byDistributor.map(dist => (
                <div key={dist.name} className="flex flex-col items-center">
                  <div className="flex items-end gap-1.5" style={{height: '220px'}}>
                    {dist.items.map(item => (
                      <div key={item.category} className="flex flex-col items-center relative">
                        <div
                          className="w-10 rounded-t-sm cursor-default transition-opacity hover:opacity-80"
                          onMouseEnter={e => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setTooltip({item, x: rect.left + rect.width / 2, y: rect.top});
                          }}
                          onMouseLeave={() => setTooltip(null)}
                          style={{
                            height: `${Math.max((item.count / maxCount) * 210, 4)}px`,
                            backgroundColor: categoryColorMap[item.category],
                          }}
                        />
                        <span className="text-xs text-gray-500 mt-1">{item.count}</span>
                      </div>
                    ))}
                  </div>
                  <div className="text-xs font-semibold text-gray-700 text-center mt-2 max-w-28 leading-tight">{dist.name}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{dist.totalCount} total</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabla resumen detallada */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Detalle por distribuidor y producto</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-3 py-2 text-left text-gray-600 font-semibold">Distribuidor</th>
                    <th className="border border-gray-200 px-3 py-2 text-left text-gray-600 font-semibold">Producto</th>
                    <th className="border border-gray-200 px-3 py-2 text-left text-gray-600 font-semibold">Vigencia</th>
                    <th className="border border-gray-200 px-3 py-2 text-right text-gray-600 font-semibold">Cantidad</th>
                    <th className="border border-gray-200 px-3 py-2 text-right text-gray-600 font-semibold">Monto Unit.</th>
                    <th className="border border-gray-200 px-3 py-2 text-right text-gray-600 font-semibold">Total Cobrado</th>
                  </tr>
                </thead>
                <tbody>
                  {byDistributor.map((dist) => {
                    const distTotal = dist.items.reduce((s, i) => s + i.totalAmount, 0);
                    const distCount = dist.items.reduce((s, i) => s + i.count, 0);
                    return [
                      ...dist.items.map((item, ii) => (
                        <tr key={`${dist.name}-${item.category}`} className="hover:bg-gray-50">
                          {ii === 0 && (
                            <td className="border border-gray-200 px-3 py-2 font-semibold text-gray-800 align-top" rowSpan={dist.items.length + 1}>
                              {dist.name}
                            </td>
                          )}
                          <td className="border border-gray-200 px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{backgroundColor: categoryColorMap[item.category]}}/>
                              {item.productName}
                            </div>
                          </td>
                          <td className="border border-gray-200 px-3 py-2 text-gray-600">
                            {item.validity ? validityLabel(item.validity) : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="border border-gray-200 px-3 py-2 text-right font-bold text-gray-800">{item.count}</td>
                          <td className="border border-gray-200 px-3 py-2 text-right text-gray-700">{formatters.cash(item.unitAmount)}</td>
                          <td className="border border-gray-200 px-3 py-2 text-right font-semibold text-gray-800">{formatters.cash(item.totalAmount)}</td>
                        </tr>
                      )),
                      <tr key={`${dist.name}-total`} className="bg-gray-100">
                        <td className="border border-gray-200 px-3 py-2 text-xs text-gray-500 italic" colSpan={2}>Total {dist.name}</td>
                        <td className="border border-gray-200 px-3 py-2 text-right font-bold text-gray-900">{distCount}</td>
                        <td className="border border-gray-200 px-3 py-2"/>
                        <td className="border border-gray-200 px-3 py-2 text-right font-bold text-gray-900">{formatters.cash(distTotal)}</td>
                      </tr>
                    ];
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tabla totales por producto */}
          <div className="mt-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Totales por producto y vigencia</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-3 py-2 text-left text-gray-600 font-semibold">Producto</th>
                    <th className="border border-gray-200 px-3 py-2 text-left text-gray-600 font-semibold">Vigencia</th>
                    <th className="border border-gray-200 px-3 py-2 text-right text-gray-600 font-semibold">Monto Unit.</th>
                    <th className="border border-gray-200 px-3 py-2 text-right text-gray-600 font-semibold">Cantidad</th>
                    <th className="border border-gray-200 px-3 py-2 text-right text-gray-600 font-semibold">Total Cobrado</th>
                  </tr>
                </thead>
                <tbody>
                  {byProduct.map((item, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="border border-gray-200 px-3 py-2 text-gray-800">{item.productName}</td>
                      <td className="border border-gray-200 px-3 py-2 text-gray-600">
                        {item.validity ? validityLabel(item.validity) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="border border-gray-200 px-3 py-2 text-right text-gray-700">{formatters.cash(item.unitAmount)}</td>
                      <td className="border border-gray-200 px-3 py-2 text-right font-bold text-gray-800">{item.count}</td>
                      <td className="border border-gray-200 px-3 py-2 text-right font-semibold text-gray-800">{formatters.cash(item.totalAmount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100">
                    <td className="border border-gray-200 px-3 py-2 font-bold text-gray-900" colSpan={3}>Total general</td>
                    <td className="border border-gray-200 px-3 py-2 text-right font-bold text-gray-900">
                      {byProduct.reduce((s, i) => s + i.count, 0)}
                    </td>
                    <td className="border border-gray-200 px-3 py-2 text-right font-bold text-gray-900">
                      {formatters.cash(byProduct.reduce((s, i) => s + i.totalAmount, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
});